import { useTranslation } from 'react-i18next';
import { Box, Button, Container, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface ErrorFallbackProps {
  error?: Error;
  resetError?: () => void;
}

/**
 * Error Fallback Component - User-friendly error display for ErrorBoundary.
 * 
 * Features:
 * - Localized error messages (PT/EN)
 * - Material UI styled components
 * - Reset button to recover
 * - Responsive design
 * - Error details hidden in production
 * 
 * @author People Rock Development Team
 * @version 1.0.0 (Phase 5)
 */
export function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const { t } = useTranslation();

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          textAlign: 'center',
          py: 4,
        }}
      >
        <ErrorOutlineIcon
          sx={{
            fontSize: 80,
            color: 'error.main',
            mb: 3,
          }}
        />

        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          {t('system.error.generic', 'Something went wrong')}
        </Typography>

        <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
          {t(
            'error.boundary.message',
            'We apologize for the inconvenience. Please try refreshing the page or contact support if the problem persists.'
          )}
        </Typography>

        {resetError && (
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={resetError}
            sx={{ minWidth: 200 }}
          >
            {t('button.tryAgain', 'Try Again')}
          </Button>
        )}

        {/* Show error details in development mode only */}
        {import.meta.env.DEV && error && (
          <Box
            sx={{
              mt: 4,
              p: 2,
              backgroundColor: 'grey.100',
              borderRadius: 1,
              width: '100%',
              textAlign: 'left',
            }}
          >
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              Error Details (Development Only):
            </Typography>
            <Typography
              variant="body2"
              component="pre"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                overflow: 'auto',
                maxHeight: 200,
                backgroundColor: 'white',
                p: 1,
                borderRadius: 1,
                border: 1,
                borderColor: 'grey.300',
              }}
            >
              {error.toString()}
              {error.stack && `\n\n${error.stack}`}
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 4 }}>
          <Button variant="text" color="primary" href="/">
            {t('menu.home', 'Go to Home')}
          </Button>
        </Box>
      </Box>
    </Container>
  );
}

export default ErrorFallback;
